import { useState, useId } from "react";
import {
  Eyebrow,
  Page,
  Btn,
  href,
  Buddy,
} from "../core";
import {
  Share2,
  TrendingUp,
  Percent,
  CheckCircle2,
  ShieldCheck,
  ChevronDown,
  Building,
  Laptop,
  Briefcase,
  Sparkles,
  ArrowRight,
  Sliders,
  DollarSign,
  Gift,
  HelpCircle,
  FileText,
  Clock,
  QrCode,
  Users,
} from "lucide-react";
import { AffiliatePortal } from "@/components/affiliate/AffiliatePortal";
import { getStoredAffiliate } from "@/lib/affiliate";

const FAQS = [
  {
    q: "What is the That Korner Affiliate Programme?",
    a: "It is a referral programme that allows eligible partners to introduce That Korner to potential customers and earn a commission when those referrals make their first eligible payment.",
  },
  {
    q: "How much commission can I earn?",
    a: "You earn 5% of the first eligible payment received from a customer attributed to your referral.",
  },
  {
    q: "Is the commission recurring?",
    a: "No. Commission applies to the first eligible payment only. Renewals and subsequent payments are excluded.",
  },
  {
    q: "How do I track my referrals?",
    a: "You receive a unique referral link and referral code. Your dashboard displays referral activity, conversions, and commission status according to the programme's tracking rules.",
  },
  {
    q: "Is there a fee to join?",
    a: "No. Registration is completely free with no hidden charges or sales minimums.",
  },
  {
    q: "Can I refer multiple businesses?",
    a: "Yes. You can refer multiple eligible businesses, subject to the programme terms and referral attribution rules.",
  },
  {
    q: "When will I receive my commission?",
    a: "Approved commissions are paid according to the programme's published payout schedule, minimum payout threshold, and applicable payment review period.",
  },
  {
    q: "What happens if a customer's payment is refunded?",
    a: "Commissions associated with refunded, cancelled, or charged-back payments may be reversed according to the programme terms.",
  },
  {
    q: "Can I promote That Korner on social media?",
    a: "Yes. You may promote That Korner through permitted channels, provided your content is accurate, follows the programme terms, and clearly discloses your affiliate relationship where required.",
  },
  {
    q: "How do I get started?",
    a: "Register on the affiliate page, verify your email address, access your dashboard, and start sharing your referral link or code.",
  },
];

export default function AffiliatePage() {
  const [isPortalOpen, setIsPortalOpen] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [calcInput, setCalcInput] = useState<number>(20000);

  const calculatedCommission = Math.round(calcInput * 0.05);

  const toggleFaq = (idx: number) => {
    setOpenFaq(openFaq === idx ? null : idx);
  };

  const handleOpenPortal = () => {
    setIsPortalOpen(true);
  };

  return (
    <Page
      current="/affiliate"
      contact={{
        title: (
          <>
            Partner with That Korner. <em>Let’s elevate hospitality.</em>
          </>
        ),
        lede: "Have questions about custom corporate partnership tiers or multi-unit restaurant portfolio referrals? Connect directly with our affiliate partnerships team.",
      }}
    >
      {/* =========================================================================
          SECTION 1: HERO SECTION
          ========================================================================= */}
      <section
        className="tk-hero tk-hero--ink relative overflow-hidden"
        style={{
          background:
            "radial-gradient(ellipse 80% 50% at 50% -20%, rgba(240, 31, 14, 0.22), transparent 70%), var(--ink)",
        }}
      >
        <div className="tk-wrap tk-hero__grid" style={{ alignItems: "center" }}>
          <div className="tk-stack tk-stack--lg">
            <div>
              <Eyebrow>THAT KORNER AFFILIATE PROGRAMME</Eyebrow>
              <h1 className="tk-h1" style={{ fontSize: "clamp(2.75rem, 5.2vw, 4.25rem)", lineHeight: 1.05 }}>
                Your Network. Your Referrals. <em style={{ fontStyle: "normal", color: "var(--red)" }}>Your Rewards.</em>
              </h1>
            </div>

            <p className="tk-lede" style={{ maxWidth: "56ch" }}>
              Help restaurants discover a smarter way to manage guest experiences and restaurant operations with That
              Korner. Introduce our platform to your network and earn a <strong>5% commission</strong> on the first
              eligible payment from every successful referral.
            </p>

            <div className="tk-row" style={{ flexWrap: "wrap", gap: "14px" }}>
              <button
                type="button"
                onClick={handleOpenPortal}
                className="tk-btn tk-btn--ink"
                style={{
                  background: "var(--red)",
                  color: "#fff",
                  boxShadow: "0 8px 24px rgba(240,31,14,0.35)",
                  border: "none",
                }}
              >
                Become an Affiliate
                <ArrowRight style={{ width: 15, height: 15, marginLeft: 4 }} />
              </button>

              <a
                href="#how-it-works"
                className="tk-btn tk-btn--ghost"
                style={{ borderColor: "rgba(255,255,255,0.2)", color: "var(--cream)" }}
              >
                How It Works
              </a>
            </div>

            <div
              className="tk-row"
              style={{
                fontSize: "13px",
                color: "rgba(255, 246, 237, 0.65)",
                gap: "10px",
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <span>Free to join</span>
              <span>·</span>
              <span>Instant registration</span>
              <span>·</span>
              <span>Unique referral link and code</span>
            </div>
          </div>

          {/* Hero Visual Card / Interactive Preview */}
          <div style={{ display: "grid", justifyItems: "center", position: "relative" }}>
            <div
              style={{
                width: "100%",
                maxWidth: 420,
                background: "linear-gradient(145deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 24,
                padding: "26px",
                boxShadow: "0 24px 60px rgba(0,0,0,0.5)",
                backdropFilter: "blur(12px)",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 18 }}>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    letterSpacing: "0.22em",
                    color: "var(--red)",
                    textTransform: "uppercase",
                  }}
                >
                  Commission Spotlight
                </span>
                <span
                  style={{
                    fontSize: "10px",
                    background: "rgba(240,31,14,0.15)",
                    color: "var(--red)",
                    padding: "3px 8px",
                    borderRadius: 12,
                    fontWeight: 500,
                  }}
                >
                  Direct 5% Payout
                </span>
              </div>

              <div
                style={{
                  background: "rgba(0,0,0,0.35)",
                  borderRadius: 16,
                  padding: "18px",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px", color: "rgba(255,246,237,0.7)" }}>
                  <span>Customer's Eligible Payment</span>
                  <span style={{ fontWeight: 600, color: "#fff" }}>₹20,000</span>
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "12px",
                    color: "rgba(255,246,237,0.7)",
                    marginTop: 8,
                  }}
                >
                  <span>Your Commission Rate</span>
                  <span style={{ fontWeight: 600, color: "var(--red)" }}>5%</span>
                </div>
                <div
                  style={{
                    borderTop: "1px solid rgba(255,255,255,0.08)",
                    marginTop: 12,
                    paddingTop: 12,
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                  }}
                >
                  <span style={{ fontSize: "13px", fontWeight: 500, color: "var(--cream)" }}>Your Commission:</span>
                  <span
                    style={{
                      fontFamily: "var(--font-sans, system-ui)",
                      fontSize: "26px",
                      fontWeight: 700,
                      color: "#fff",
                    }}
                  >
                    ₹1,000
                  </span>
                </div>
              </div>

              <div style={{ marginTop: 18 }}>
                <button
                  type="button"
                  onClick={handleOpenPortal}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 8,
                    padding: "12px",
                    borderRadius: 12,
                    background: "rgba(255,255,255,0.08)",
                    border: "1px solid rgba(255,255,255,0.12)",
                    color: "var(--cream)",
                    fontSize: "12px",
                    fontWeight: 500,
                    cursor: "pointer",
                    transition: "all 0.2s ease",
                  }}
                >
                  <Share2 style={{ width: 14, height: 14, color: "var(--red)" }} />
                  Get Your Referral Link in 30 Seconds
                </button>
              </div>

              <p style={{ marginTop: 12, fontSize: "11px", color: "rgba(255,246,237,0.45)", textAlign: "center" }}>
                Paid on first eligible payment collected · No caps
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          SECTION 2: INTRODUCTION TO THAT KORNER (4 PRODUCT CARDS)
          ========================================================================= */}
      <section className="tk-sec tk-sec--char" id="platform-intro">
        <div className="tk-wrap">
          <div className="tk-stack tk-stack--lg" style={{ maxWidth: "780px", marginBottom: "48px" }}>
            <Eyebrow>THE PLATFORM BEHIND THE PROGRAMME</Eyebrow>
            <h2 className="tk-h2">
              A Smarter Approach to <em>Restaurant Technology.</em>
            </h2>
            <p className="tk-lede" style={{ color: "rgba(255, 246, 237, 0.75)" }}>
              That Korner brings together technology designed to help restaurants improve menu discovery, understand
              business performance, personalise guest interactions, and manage essential operations. As an affiliate,
              you can introduce restaurant owners, hospitality professionals, and business operators to tools designed
              for the evolving needs of the hospitality industry.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: "24px",
            }}
          >
            {/* PRODUCT CARD 1 — CHEF BUDDY */}
            <div
              className="tk-card"
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 20,
                padding: "28px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                transition: "transform 0.2s ease, border-color 0.2s ease",
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    letterSpacing: "0.2em",
                    color: "var(--red)",
                    textTransform: "uppercase",
                  }}
                >
                  PRODUCT CARD 1 · CHEF BUDDY
                </span>
                <h3 style={{ fontSize: "20px", fontWeight: 600, color: "var(--cream)", marginTop: 10, marginBottom: 12 }}>
                  Give Every Menu a Voice.
                </h3>
                <p style={{ fontSize: "14px", lineHeight: "1.6", color: "rgba(255,246,237,0.7)" }}>
                  Chef Buddy helps restaurant guests explore menus, discover dishes, understand ingredients, and receive
                  relevant recommendations through a conversational digital menu experience.
                </p>
              </div>
              <div style={{ marginTop: 24 }}>
                <a
                  href={href("/chef-buddy")}
                  className="tk-row"
                  style={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "var(--red)",
                    alignItems: "center",
                    gap: 6,
                    textDecoration: "none",
                  }}
                >
                  Explore Chef Buddy <ArrowRight style={{ width: 14, height: 14 }} />
                </a>
              </div>
            </div>

            {/* PRODUCT CARD 2 — CHEF BRAIN */}
            <div
              className="tk-card"
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 20,
                padding: "28px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                transition: "transform 0.2s ease, border-color 0.2s ease",
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    letterSpacing: "0.2em",
                    color: "var(--red)",
                    textTransform: "uppercase",
                  }}
                >
                  PRODUCT CARD 2 · CHEF BRAIN
                </span>
                <h3 style={{ fontSize: "20px", fontWeight: 600, color: "var(--cream)", marginTop: 10, marginBottom: 12 }}>
                  Turn Menu Data Into Better Decisions.
                </h3>
                <p style={{ fontSize: "14px", lineHeight: "1.6", color: "rgba(255,246,237,0.7)" }}>
                  Chef Brain helps restaurant teams analyse menu performance, understand profitability, and identify
                  opportunities to make more informed menu decisions.
                </p>
              </div>
              <div style={{ marginTop: 24 }}>
                <a
                  href={href("/chef-brain")}
                  className="tk-row"
                  style={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "var(--red)",
                    alignItems: "center",
                    gap: 6,
                    textDecoration: "none",
                  }}
                >
                  Explore Chef Brain <ArrowRight style={{ width: 14, height: 14 }} />
                </a>
              </div>
            </div>

            {/* PRODUCT CARD 3 — KORNER ASK */}
            <div
              className="tk-card"
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 20,
                padding: "28px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                transition: "transform 0.2s ease, border-color 0.2s ease",
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    letterSpacing: "0.2em",
                    color: "var(--red)",
                    textTransform: "uppercase",
                  }}
                >
                  PRODUCT CARD 3 · KORNER ASK
                </span>
                <h3 style={{ fontSize: "20px", fontWeight: 600, color: "var(--cream)", marginTop: 10, marginBottom: 12 }}>
                  Make Guest Experiences More Personal.
                </h3>
                <p style={{ fontSize: "14px", lineHeight: "1.6", color: "rgba(255,246,237,0.7)" }}>
                  Korner Ask supports restaurant teams with relevant guest preference insights, helping them deliver
                  more informed and personalised service.
                </p>
              </div>
              <div style={{ marginTop: 24 }}>
                <a
                  href={href("/korner-ask")}
                  className="tk-row"
                  style={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "var(--red)",
                    alignItems: "center",
                    gap: 6,
                    textDecoration: "none",
                  }}
                >
                  Explore Korner Ask <ArrowRight style={{ width: 14, height: 14 }} />
                </a>
              </div>
            </div>

            {/* PRODUCT CARD 4 — THAT KORNER PLATFORM */}
            <div
              className="tk-card"
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 20,
                padding: "28px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                transition: "transform 0.2s ease, border-color 0.2s ease",
              }}
            >
              <div>
                <span
                  style={{
                    fontSize: "11px",
                    fontWeight: 600,
                    letterSpacing: "0.2em",
                    color: "var(--red)",
                    textTransform: "uppercase",
                  }}
                >
                  PRODUCT CARD 4 · THAT KORNER PLATFORM
                </span>
                <h3 style={{ fontSize: "20px", fontWeight: 600, color: "var(--cream)", marginTop: 10, marginBottom: 12 }}>
                  Bring Restaurant Operations Together.
                </h3>
                <p style={{ fontSize: "14px", lineHeight: "1.6", color: "rgba(255,246,237,0.7)" }}>
                  That Korner provides digital tools designed to support restaurant workflows, from guest-facing
                  experiences to essential operational processes.
                </p>
              </div>
              <div style={{ marginTop: 24 }}>
                <a
                  href={href("/platform")}
                  className="tk-row"
                  style={{
                    fontSize: "13px",
                    fontWeight: 500,
                    color: "var(--red)",
                    alignItems: "center",
                    gap: 6,
                    textDecoration: "none",
                  }}
                >
                  Explore the Platform <ArrowRight style={{ width: 14, height: 14 }} />
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          SECTION 3: COMMISSION STRUCTURE & INTERACTIVE CALCULATOR
          ========================================================================= */}
      <section className="tk-sec tk-sec--ink" id="commission">
        <div className="tk-wrap">
          <div className="tk-stack tk-stack--lg" style={{ maxWidth: "780px" }}>
            <Eyebrow>SIMPLE. TRANSPARENT. REWARDING.</Eyebrow>
            <h2 className="tk-h2">
              Earn 5% on the <em>First Eligible Payment.</em>
            </h2>
            <p className="tk-lede" style={{ color: "rgba(255, 246, 237, 0.75)" }}>
              Every successful referral gives you an opportunity to earn. When a business you refer becomes a That Korner
              customer and makes its first eligible payment, you receive a 5% commission on the eligible payment
              amount.
            </p>
          </div>

          <div
            style={{
              marginTop: "44px",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
              gap: "32px",
              alignItems: "stretch",
            }}
          >
            {/* Interactive Live Commission Calculator */}
            <div
              style={{
                background: "rgba(255,255,255,0.03)",
                border: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 24,
                padding: "32px",
              }}
            >
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.2em", color: "var(--red)", textTransform: "uppercase" }}>
                  COMMISSION EXAMPLE & CALCULATOR
                </span>
                <span style={{ fontSize: "11px", color: "rgba(255,246,237,0.5)" }}>Live Estimate</span>
              </div>

              <div style={{ marginTop: 24 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <label style={{ fontSize: "13px", color: "rgba(255,246,237,0.7)" }}>
                    Customer's Eligible Payment:
                  </label>
                  <span
                    style={{
                      fontFamily: "var(--font-sans, system-ui)",
                      fontSize: "20px",
                      fontWeight: 700,
                      color: "#fff",
                    }}
                  >
                    ₹{calcInput.toLocaleString("en-IN")}
                  </span>
                </div>

                <input
                  type="range"
                  min="5000"
                  max="150000"
                  step="5000"
                  value={calcInput}
                  onChange={(e) => setCalcInput(Number(e.target.value))}
                  style={{
                    width: "100%",
                    marginTop: 14,
                    accentColor: "var(--red)",
                    cursor: "pointer",
                  }}
                />

                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "11px",
                    color: "rgba(255,246,237,0.4)",
                    marginTop: 4,
                  }}
                >
                  <span>₹5,000</span>
                  <span style={{ color: "var(--red)" }}>₹20,000 (Standard)</span>
                  <span>₹150,000</span>
                </div>
              </div>

              <div
                style={{
                  marginTop: 24,
                  background: "rgba(0,0,0,0.3)",
                  borderRadius: 16,
                  padding: "20px",
                  border: "1px solid rgba(255,255,255,0.06)",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", color: "rgba(255,246,237,0.7)" }}>
                  <span>Your Commission Rate:</span>
                  <span style={{ fontWeight: 600, color: "var(--red)" }}>5%</span>
                </div>

                <div
                  style={{
                    marginTop: 14,
                    paddingTop: 14,
                    borderTop: "1px solid rgba(255,255,255,0.08)",
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "baseline",
                  }}
                >
                  <span style={{ fontSize: "15px", fontWeight: 600, color: "var(--cream)" }}>
                    Your Commission:
                  </span>
                  <span
                    style={{
                      fontFamily: "var(--font-sans, system-ui)",
                      fontSize: "30px",
                      fontWeight: 700,
                      color: "#fff",
                    }}
                  >
                    ₹{calculatedCommission.toLocaleString("en-IN")}
                  </span>
                </div>
              </div>

              <div style={{ marginTop: 24 }}>
                <button
                  type="button"
                  onClick={handleOpenPortal}
                  className="tk-btn"
                  style={{
                    width: "100%",
                    background: "var(--red)",
                    color: "#fff",
                    border: "none",
                    justifyContent: "center",
                  }}
                >
                  Start Referring Businesses
                  <ArrowRight style={{ width: 15, height: 15, marginLeft: 6 }} />
                </button>
              </div>
            </div>

            {/* Commission Rules & Fine Print */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "space-between",
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.06)",
                borderRadius: 24,
                padding: "32px",
              }}
            >
              <div className="tk-stack tk-stack--md">
                <span style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.2em", color: "rgba(255,246,237,0.6)", textTransform: "uppercase" }}>
                  TRANSPARENCY & TERMS
                </span>

                <div style={{ display: "flex", gap: "14px", alignItems: "flex-start", marginTop: 8 }}>
                  <ShieldCheck style={{ width: 22, height: 22, color: "var(--red)", flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <h4 style={{ fontSize: "15px", fontWeight: 600, color: "var(--cream)", marginBottom: 4 }}>
                      Calculation Basis
                    </h4>
                    <p style={{ fontSize: "13px", lineHeight: 1.6, color: "rgba(255,246,237,0.7)" }}>
                      Your commission is calculated on the eligible amount actually collected, excluding applicable
                      taxes, refunds, cancellations, and chargebacks.
                    </p>
                  </div>
                </div>

                <div
                  style={{
                    marginTop: 16,
                    padding: "16px 20px",
                    borderRadius: 14,
                    background: "rgba(240, 31, 14, 0.08)",
                    border: "1px solid rgba(240, 31, 14, 0.25)",
                  }}
                >
                  <p style={{ fontSize: "13px", fontWeight: 600, color: "var(--cream)", marginBottom: 4 }}>
                    Important Note:
                  </p>
                  <p style={{ fontSize: "12.5px", lineHeight: 1.6, color: "rgba(255,246,237,0.8)" }}>
                    The programme pays commission on the first eligible payment only. Renewals and subsequent payments do
                    not generate additional commission.
                  </p>
                </div>

                <div style={{ display: "flex", gap: "14px", alignItems: "flex-start", marginTop: 12 }}>
                  <CheckCircle2 style={{ width: 20, height: 20, color: "var(--red)", flexShrink: 0, marginTop: 2 }} />
                  <div>
                    <h4 style={{ fontSize: "14px", fontWeight: 500, color: "var(--cream)", marginBottom: 2 }}>
                      No Maximum Cap
                    </h4>
                    <p style={{ fontSize: "12.5px", color: "rgba(255,246,237,0.65)" }}>
                      Whether you refer 1 restaurant or 50, each qualifying first eligible payment generates your full 5% commission.
                    </p>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 24, paddingTop: 20, borderTop: "1px solid rgba(255,255,255,0.08)" }}>
                <p style={{ fontSize: "12px", color: "rgba(255,246,237,0.5)" }}>
                  Need enterprise attribution for multi-city hotel or restaurant chains? Reach out via our partner contact band.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================================================================
          SECTION 4: HOW IT WORKS (FOUR STEPS)
          ========================================================================= */}
      <section className="tk-sec tk-sec--char" id="how-it-works">
        <div className="tk-wrap">
          <div className="tk-stack tk-stack--lg" style={{ maxWidth: "780px", marginBottom: "48px" }}>
            <Eyebrow>GET STARTED IN FOUR STEPS</Eyebrow>
            <h2 className="tk-h2">
              From Introduction to <em>Commission.</em>
            </h2>
            <p className="tk-lede" style={{ color: "rgba(255, 246, 237, 0.75)" }}>
              Getting started is straightforward. Join the programme, share your referral details, and track your
              results from your affiliate dashboard.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
              gap: "24px",
            }}
          >
            {/* STEP 1 */}
            <div
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 20,
                padding: "28px",
                position: "relative",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "rgba(240,31,14,0.15)",
                  color: "var(--red)",
                  fontWeight: 700,
                  fontSize: "14px",
                  marginBottom: 16,
                }}
              >
                01
              </div>
              <span style={{ display: "block", fontSize: "11px", fontWeight: 600, letterSpacing: "0.2em", color: "var(--red)", textTransform: "uppercase" }}>
                STEP 1 — REGISTER
              </span>
              <h3 style={{ fontSize: "18px", fontWeight: 600, color: "var(--cream)", marginTop: 8, marginBottom: 10 }}>
                Create Your Affiliate Account.
              </h3>
              <p style={{ fontSize: "13.5px", lineHeight: 1.6, color: "rgba(255,246,237,0.7)" }}>
                Sign up with your basic details and verify your email address to activate your affiliate account.
              </p>
            </div>

            {/* STEP 2 */}
            <div
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 20,
                padding: "28px",
                position: "relative",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "rgba(240,31,14,0.15)",
                  color: "var(--red)",
                  fontWeight: 700,
                  fontSize: "14px",
                  marginBottom: 16,
                }}
              >
                02
              </div>
              <span style={{ display: "block", fontSize: "11px", fontWeight: 600, letterSpacing: "0.2em", color: "var(--red)", textTransform: "uppercase" }}>
                STEP 2 — GET YOUR REFERRAL DETAILS
              </span>
              <h3 style={{ fontSize: "18px", fontWeight: 600, color: "var(--cream)", marginTop: 8, marginBottom: 10 }}>
                Your Link. Your Code.
              </h3>
              <p style={{ fontSize: "13.5px", lineHeight: 1.6, color: "rgba(255,246,237,0.7)" }}>
                Receive a unique referral link and referral code that you can share with your professional network.
              </p>
            </div>

            {/* STEP 3 */}
            <div
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 20,
                padding: "28px",
                position: "relative",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "rgba(240,31,14,0.15)",
                  color: "var(--red)",
                  fontWeight: 700,
                  fontSize: "14px",
                  marginBottom: 16,
                }}
              >
                03
              </div>
              <span style={{ display: "block", fontSize: "11px", fontWeight: 600, letterSpacing: "0.2em", color: "var(--red)", textTransform: "uppercase" }}>
                STEP 3 — REFER A BUSINESS
              </span>
              <h3 style={{ fontSize: "18px", fontWeight: 600, color: "var(--cream)", marginTop: 8, marginBottom: 10 }}>
                Make the Right Introduction.
              </h3>
              <p style={{ fontSize: "13.5px", lineHeight: 1.6, color: "rgba(255,246,237,0.7)" }}>
                Introduce That Korner to restaurant owners, hospitality consultants, and businesses that may benefit
                from the platform.
              </p>
            </div>

            {/* STEP 4 */}
            <div
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 20,
                padding: "28px",
                position: "relative",
              }}
            >
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  background: "rgba(240,31,14,0.15)",
                  color: "var(--red)",
                  fontWeight: 700,
                  fontSize: "14px",
                  marginBottom: 16,
                }}
              >
                04
              </div>
              <span style={{ display: "block", fontSize: "11px", fontWeight: 600, letterSpacing: "0.2em", color: "var(--red)", textTransform: "uppercase" }}>
                STEP 4 — EARN COMMISSION
              </span>
              <h3 style={{ fontSize: "18px", fontWeight: 600, color: "var(--cream)", marginTop: 8, marginBottom: 10 }}>
                Track Your Successful Referrals.
              </h3>
              <p style={{ fontSize: "13.5px", lineHeight: 1.6, color: "rgba(255,246,237,0.7)" }}>
                When an attributed customer makes their first eligible payment, your commission is recorded in your
                affiliate dashboard.
              </p>
            </div>
          </div>

          <div style={{ marginTop: 40, textAlign: "center" }}>
            <button
              type="button"
              onClick={handleOpenPortal}
              className="tk-btn"
              style={{
                background: "var(--red)",
                color: "#fff",
                border: "none",
                display: "inline-flex",
                margin: "0 auto",
              }}
            >
              Become an Affiliate
              <ArrowRight style={{ width: 15, height: 15, marginLeft: 6 }} />
            </button>
          </div>
        </div>
      </section>

      {/* =========================================================================
          SECTION 5: AFFILIATE DASHBOARD (5 FEATURES)
          ========================================================================= */}
      <section className="tk-sec tk-sec--ink" id="dashboard">
        <div className="tk-wrap">
          <div className="tk-stack tk-stack--lg" style={{ maxWidth: "780px", marginBottom: "48px" }}>
            <Eyebrow>YOUR AFFILIATE WORKSPACE</Eyebrow>
            <h2 className="tk-h2">
              Your Referrals. Your Earnings. <em>One Dashboard.</em>
            </h2>
            <p className="tk-lede" style={{ color: "rgba(255, 246, 237, 0.75)" }}>
              Manage your affiliate activity from one place. Access your referral details, monitor conversions, review
              commission status, and keep track of your payout history.
            </p>
          </div>

          {/* Interactive Workspace Showcase Banner */}
          <div
            style={{
              borderRadius: 24,
              border: "1px solid rgba(255,255,255,0.12)",
              background: "linear-gradient(135deg, rgba(255,255,255,0.04) 0%, rgba(240,31,14,0.06) 100%)",
              padding: "36px",
              marginBottom: "36px",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "24px",
            }}
          >
            <div>
              <span
                style={{
                  fontSize: "11px",
                  fontWeight: 600,
                  letterSpacing: "0.2em",
                  color: "var(--red)",
                  textTransform: "uppercase",
                }}
              >
                LIVE INTERACTIVE WORKSPACE
              </span>
              <h3 style={{ fontSize: "24px", fontWeight: 600, color: "#fff", marginTop: 6 }}>
                Open Your Live Affiliate Portal
              </h3>
              <p style={{ fontSize: "14px", color: "rgba(255,246,237,0.7)", maxWidth: "52ch", marginTop: 4 }}>
                Instant link generation, real-time click tracking, commission status ledger, and ready-to-use pitch collateral.
              </p>
            </div>

            <div>
              <button
                type="button"
                onClick={handleOpenPortal}
                className="tk-btn"
                style={{
                  background: "var(--red)",
                  color: "#fff",
                  border: "none",
                  boxShadow: "0 6px 20px rgba(240,31,14,0.4)",
                }}
              >
                Launch Affiliate Workspace
                <ArrowRight style={{ width: 14, height: 14, marginLeft: 6 }} />
              </button>
            </div>
          </div>

          {/* The 5 Features Grid */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: "24px",
            }}
          >
            {/* FEATURE 1 */}
            <div
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: 18,
                padding: "24px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <Share2 style={{ width: 20, height: 20, color: "var(--red)" }} />
                <h4 style={{ fontSize: "16px", fontWeight: 600, color: "var(--cream)" }}>
                  Your Referral Link
                </h4>
              </div>
              <p style={{ fontSize: "13.5px", lineHeight: 1.6, color: "rgba(255,246,237,0.7)" }}>
                Copy and share your unique referral link to introduce potential customers to That Korner.
              </p>
            </div>

            {/* FEATURE 2 */}
            <div
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: 18,
                padding: "24px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <FileText style={{ width: 20, height: 20, color: "var(--red)" }} />
                <h4 style={{ fontSize: "16px", fontWeight: 600, color: "var(--cream)" }}>
                  Your Referral Code
                </h4>
              </div>
              <p style={{ fontSize: "13.5px", lineHeight: 1.6, color: "rgba(255,246,237,0.7)" }}>
                Share your unique code when introducing businesses directly or through eligible referral channels.
              </p>
            </div>

            {/* FEATURE 3 */}
            <div
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: 18,
                padding: "24px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <TrendingUp style={{ width: 20, height: 20, color: "var(--red)" }} />
                <h4 style={{ fontSize: "16px", fontWeight: 600, color: "var(--cream)" }}>
                  Referral Performance
                </h4>
              </div>
              <p style={{ fontSize: "13.5px", lineHeight: 1.6, color: "rgba(255,246,237,0.7)" }}>
                Track referral clicks, attributed leads, converted customers, and your overall referral performance.
              </p>
            </div>

            {/* FEATURE 4 */}
            <div
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: 18,
                padding: "24px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <DollarSign style={{ width: 20, height: 20, color: "var(--red)" }} />
                <h4 style={{ fontSize: "16px", fontWeight: 600, color: "var(--cream)" }}>
                  Commission Tracking
                </h4>
              </div>
              <p style={{ fontSize: "13.5px", lineHeight: 1.6, color: "rgba(255,246,237,0.7)" }}>
                View your pending, approved, payable, paid, and reversed commissions with a clear transaction history.
              </p>
            </div>

            {/* FEATURE 5 */}
            <div
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: 18,
                padding: "24px",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 12 }}>
                <Clock style={{ width: 20, height: 20, color: "var(--red)" }} />
                <h4 style={{ fontSize: "16px", fontWeight: 600, color: "var(--cream)" }}>
                  Payout History
                </h4>
              </div>
              <p style={{ fontSize: "13.5px", lineHeight: 1.6, color: "rgba(255,246,237,0.7)" }}>
                Review completed payouts and access your available commission and payment records.
              </p>
            </div>
          </div>

          <div style={{ marginTop: 40, textAlign: "center" }}>
            <button
              type="button"
              onClick={handleOpenPortal}
              className="tk-btn"
              style={{
                background: "var(--red)",
                color: "#fff",
                border: "none",
                display: "inline-flex",
                margin: "0 auto",
              }}
            >
              Join the Programme
              <ArrowRight style={{ width: 15, height: 15, marginLeft: 6 }} />
            </button>
          </div>
        </div>
      </section>

      {/* =========================================================================
          SECTION 6: WHO CAN JOIN? (4 AUDIENCE CARDS)
          ========================================================================= */}
      <section className="tk-sec tk-sec--char" id="who-can-join">
        <div className="tk-wrap">
          <div className="tk-stack tk-stack--lg" style={{ maxWidth: "780px", marginBottom: "48px" }}>
            <Eyebrow>BUILT FOR HOSPITALITY NETWORKS</Eyebrow>
            <h2 className="tk-h2">
              Know Restaurants? <em>Let's Work Together.</em>
            </h2>
            <p className="tk-lede" style={{ color: "rgba(255, 246, 237, 0.75)" }}>
              If you have connections within the restaurant or hospitality industry, the That Korner Affiliate
              Programme gives you an opportunity to introduce businesses to relevant technology and earn commission
              on successful referrals.
            </p>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: "24px",
            }}
          >
            {/* AUDIENCE CARD 1 — HOSPITALITY CONSULTANTS */}
            <div
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 20,
                padding: "28px",
              }}
            >
              <Building style={{ width: 28, height: 28, color: "var(--red)", marginBottom: 14 }} />
              <span style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.2em", color: "var(--red)", textTransform: "uppercase" }}>
                AUDIENCE CARD 1
              </span>
              <h3 style={{ fontSize: "18px", fontWeight: 600, color: "var(--cream)", marginTop: 8, marginBottom: 10 }}>
                HOSPITALITY CONSULTANTS
              </h3>
              <p style={{ fontSize: "13.5px", lineHeight: 1.6, color: "rgba(255,246,237,0.7)" }}>
                Help the restaurants you advise discover technology that supports their guest experiences and operations.
              </p>
            </div>

            {/* AUDIENCE CARD 2 — RESTAURANT TECHNOLOGY PARTNERS */}
            <div
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 20,
                padding: "28px",
              }}
            >
              <Laptop style={{ width: 28, height: 28, color: "var(--red)", marginBottom: 14 }} />
              <span style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.2em", color: "var(--red)", textTransform: "uppercase" }}>
                AUDIENCE CARD 2
              </span>
              <h3 style={{ fontSize: "18px", fontWeight: 600, color: "var(--cream)", marginTop: 8, marginBottom: 10 }}>
                RESTAURANT TECHNOLOGY PARTNERS
              </h3>
              <p style={{ fontSize: "13.5px", lineHeight: 1.6, color: "rgba(255,246,237,0.7)" }}>
                Introduce That Korner to businesses exploring digital solutions for their restaurants.
              </p>
            </div>

            {/* AUDIENCE CARD 3 — AGENCIES AND BUSINESS ADVISORS */}
            <div
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 20,
                padding: "28px",
              }}
            >
              <Briefcase style={{ width: 28, height: 28, color: "var(--red)", marginBottom: 14 }} />
              <span style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.2em", color: "var(--red)", textTransform: "uppercase" }}>
                AUDIENCE CARD 3
              </span>
              <h3 style={{ fontSize: "18px", fontWeight: 600, color: "var(--cream)", marginTop: 8, marginBottom: 10 }}>
                AGENCIES AND BUSINESS ADVISORS
              </h3>
              <p style={{ fontSize: "13.5px", lineHeight: 1.6, color: "rgba(255,246,237,0.7)" }}>
                Connect your restaurant clients with technology that complements your existing services.
              </p>
            </div>

            {/* AUDIENCE CARD 4 — INDUSTRY PROFESSIONALS AND CREATORS */}
            <div
              style={{
                background: "rgba(255,255,255,0.02)",
                border: "1px solid rgba(255,255,255,0.08)",
                borderRadius: 20,
                padding: "28px",
              }}
            >
              <Sparkles style={{ width: 28, height: 28, color: "var(--red)", marginBottom: 14 }} />
              <span style={{ fontSize: "11px", fontWeight: 600, letterSpacing: "0.2em", color: "var(--red)", textTransform: "uppercase" }}>
                AUDIENCE CARD 4
              </span>
              <h3 style={{ fontSize: "18px", fontWeight: 600, color: "var(--cream)", marginTop: 8, marginBottom: 10 }}>
                INDUSTRY PROFESSIONALS AND CREATORS
              </h3>
              <p style={{ fontSize: "13.5px", lineHeight: 1.6, color: "rgba(255,246,237,0.7)" }}>
                Share That Korner with your professional network and relevant hospitality audience.
              </p>
            </div>
          </div>

          <div style={{ marginTop: 40, textAlign: "center" }}>
            <button
              type="button"
              onClick={handleOpenPortal}
              className="tk-btn"
              style={{
                background: "var(--red)",
                color: "#fff",
                border: "none",
                display: "inline-flex",
                margin: "0 auto",
              }}
            >
              Join the Affiliate Programme
              <ArrowRight style={{ width: 15, height: 15, marginLeft: 6 }} />
            </button>
          </div>
        </div>
      </section>

      {/* =========================================================================
          SECTION 7: FREQUENTLY ASKED QUESTIONS (Q1 TO Q10)
          ========================================================================= */}
      <section className="tk-sec tk-sec--ink" id="faq">
        <div className="tk-wrap" style={{ maxWidth: "880px" }}>
          <div className="tk-stack tk-stack--lg" style={{ textAlign: "center", marginBottom: "48px" }}>
            <Eyebrow>FREQUENTLY ASKED QUESTIONS</Eyebrow>
            <h2 className="tk-h2">
              Everything You Need to <em>Know.</em>
            </h2>
            <p className="tk-lede" style={{ color: "rgba(255, 246, 237, 0.75)", margin: "0 auto" }}>
              Clear answers to common questions about eligibility, tracking, commission payment schedules, and terms.
            </p>
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            {FAQS.map((faq, idx) => {
              const isOpen = openFaq === idx;
              return (
                <div
                  key={faq.q}
                  style={{
                    borderRadius: 16,
                    border: isOpen ? "1px solid rgba(240,31,14,0.3)" : "1px solid rgba(255,255,255,0.08)",
                    background: isOpen ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.015)",
                    overflow: "hidden",
                    transition: "all 0.2s ease",
                  }}
                >
                  <button
                    type="button"
                    onClick={() => toggleFaq(idx)}
                    style={{
                      width: "100%",
                      padding: "20px 24px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 16,
                      background: "transparent",
                      border: "none",
                      color: "var(--cream)",
                      textAlign: "left",
                      fontSize: "16px",
                      fontWeight: 500,
                      cursor: "pointer",
                    }}
                  >
                    <span>
                      <strong style={{ color: "var(--red)", marginRight: 8 }}>Q{idx + 1}.</strong>
                      {faq.q}
                    </span>
                    <ChevronDown
                      style={{
                        width: 18,
                        height: 18,
                        color: isOpen ? "var(--red)" : "rgba(255,246,237,0.5)",
                        transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.2s ease",
                        flexShrink: 0,
                      }}
                    />
                  </button>
                  {isOpen && (
                    <div
                      style={{
                        padding: "0 24px 22px 24px",
                        fontSize: "14px",
                        lineHeight: 1.65,
                        color: "rgba(255,246,237,0.75)",
                        borderTop: "1px solid rgba(255,255,255,0.04)",
                        paddingTop: 16,
                      }}
                    >
                      {faq.a}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* =========================================================================
          SECTION 8: FINAL CALL TO ACTION
          ========================================================================= */}
      <section
        className="tk-sec tk-sec--red"
        style={{
          background: "linear-gradient(180deg, #A81408 0%, #7A0A02 100%)",
          color: "#fff",
          textAlign: "center",
          padding: "90px 20px",
        }}
      >
        <div className="tk-wrap" style={{ maxWidth: "720px", margin: "0 auto" }}>
          <span
            style={{
              fontSize: "12px",
              fontWeight: 700,
              letterSpacing: "0.25em",
              textTransform: "uppercase",
              color: "rgba(255,255,255,0.8)",
            }}
          >
            LET'S GROW TOGETHER
          </span>
          <h2
            style={{
              fontSize: "clamp(2.2rem, 4.2vw, 3.4rem)",
              fontWeight: 400,
              lineHeight: 1.15,
              marginTop: 14,
              marginBottom: 16,
            }}
          >
            Your Network Can Open New Doors for Hospitality.
          </h2>
          <p
            style={{
              fontSize: "16px",
              lineHeight: 1.6,
              color: "rgba(255,255,255,0.85)",
              marginBottom: 32,
            }}
          >
            Introduce That Korner to your network of restaurant owners and hospitality professionals. Share your
            referral link, track your results, and earn 5% commission when your referrals make their first eligible
            payment.
          </p>

          <div
            style={{
              display: "flex",
              justifyContent: "center",
              flexWrap: "wrap",
              gap: 16,
            }}
          >
            <button
              type="button"
              onClick={handleOpenPortal}
              style={{
                background: "#fff",
                color: "#9A1208",
                padding: "14px 32px",
                borderRadius: 999,
                fontWeight: 600,
                fontSize: "14px",
                border: "none",
                cursor: "pointer",
                boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
                transition: "all 0.2s ease",
              }}
            >
              Become an Affiliate
            </button>

            <button
              type="button"
              onClick={handleOpenPortal}
              style={{
                background: "transparent",
                color: "#fff",
                padding: "14px 28px",
                borderRadius: 999,
                fontWeight: 500,
                fontSize: "14px",
                border: "1px solid rgba(255,255,255,0.4)",
                cursor: "pointer",
                transition: "all 0.2s ease",
              }}
            >
              Already Registered? Log In
            </button>
          </div>

          <p
            style={{
              marginTop: 24,
              fontSize: "12.5px",
              color: "rgba(255,255,255,0.7)",
            }}
          >
            Join the programme for free and start building your referral network.
          </p>
        </div>
      </section>

      {/* The Affiliate Workspace Modal */}
      <AffiliatePortal isOpen={isPortalOpen} onClose={() => setIsPortalOpen(false)} />
    </Page>
  );
}
